import Vue from "vue";
import BuildingService from "@/services/building";
import { fetchSessionUser } from "@/services/session";
import { DEFAULT_MAP_CENTER } from "@/constants/mapDefaults";

import {
  FETCH_PROBLEMS,
  FETCH_BUILDINGS,
  FETCH_SESSION,
  SET_PROBLEMS,
  SET_BUILDINGS,
  SET_MAP,
  SET_MARKERS,
  SET_IS_MOBILE,
  FETCH_CURRENT_BUILDING,
  SET_CURRENT_BUILDING,
  SET_SESSION_USER,
  DISCARD_FILTERS,
  FILTER_BY_PROBLEM,
  SHOW_PROBLEM_LAYERS_BY_IDS,
} from "@/store/actions.type";

let initialState = {
  eventBus: new Vue(),
  isMobile: window.innerWidth <= 390,
  map: null,
  center: DEFAULT_MAP_CENTER,
  markers: [],
  problems: [],
  buildings: [],
  ids: {} /* cache all buildings after fetch: id => object */,
  currentBuilding: null,
  /** `GET /me` payload or null if anonymous / error */
  sessionUser: null,
};

const DELIMITER = ",";

function isDraft(building) {
  return building != null && Number(building.hidden) !== 0;
}

function visibleBuildings(buildings, sessionUser) {
  const list = Array.isArray(buildings) ? buildings : [];
  if (sessionUser?.is_editor) {
    return list;
  }
  return list.filter((building) => !isDraft(building));
}

function rebuildBuildingIndex(state, buildings) {
  state.ids = {};
  buildings.forEach((item) => {
    state.ids[parseInt(item.id, 10)] = item;
  });
}

export const state = Object.assign({}, initialState);

export const actions = {
  [FETCH_PROBLEMS](context) {
    return BuildingService.getProblems().then((data) => {
      return context.commit(SET_PROBLEMS, data);
    });
  },
  [FETCH_BUILDINGS](context) {
    return BuildingService.get().then((data) => {
      return context.commit(SET_BUILDINGS, data);
    });
  },
  [FETCH_SESSION](context) {
    return fetchSessionUser().then((user) => {
      context.commit(SET_SESSION_USER, user);
      return user;
    });
  },
  [FETCH_CURRENT_BUILDING](context, id) {
    if (id == null || `${id}`.trim() === '') {
      context.commit(SET_CURRENT_BUILDING, null);
      return Promise.resolve(null);
    }

    const parsedId = parseInt(id, 10);
    const cached = context.state.ids[parsedId] || null;
    if (cached) {
      context.commit(SET_CURRENT_BUILDING, cached);
      return Promise.resolve(cached);
    }

    return BuildingService.get(id).then((building) => {
      if (!building || Array.isArray(building)) {
        context.commit(SET_CURRENT_BUILDING, null);
        return null;
      }

      if (!context.state.sessionUser?.is_editor && isDraft(building)) {
        context.commit(SET_CURRENT_BUILDING, null);
        return null;
      }

      if (context.state.sessionUser?.is_editor) {
        context.state.ids[parsedId] = building;
      }

      context.commit(SET_CURRENT_BUILDING, building);
      return building;
    });
  },
};

export const mutations = {
  [SET_IS_MOBILE](state, isMobile) {
    state.isMobile = isMobile;
  },
  [SET_PROBLEMS](state, problems) {
    state.problems = problems;
  },
  [SET_BUILDINGS](state, buildings) {
    const visible = visibleBuildings(buildings, state.sessionUser);
    state.buildings = visible;
    rebuildBuildingIndex(state, visible);
  },
  [SET_MAP](state, map) {
    state.map = map;
  },
  [SET_MARKERS](state, markers) {
    state.markers = markers;
  },
  [SET_CURRENT_BUILDING](state, building) {
    state.currentBuilding = building ? { ...building } : null;
  },
  [SET_SESSION_USER](state, user) {
    state.sessionUser = user;
    state.buildings = visibleBuildings(state.buildings, user);
    rebuildBuildingIndex(state, state.buildings);
    if (
      state.currentBuilding &&
      !user?.is_editor &&
      isDraft(state.currentBuilding)
    ) {
      state.currentBuilding = null;
    }
  },
  [DISCARD_FILTERS](state, visibility) {
    state.markers.map((marker) => {
      marker.setVisible(visibility);
    });
  },
  [FILTER_BY_PROBLEM](state, { visibility, problemId }) {
    state.markers.map((marker) => {
      if (marker.problemId === problemId) marker.setVisible(visibility);
    });
  },
  [SHOW_PROBLEM_LAYERS_BY_IDS](state, { visibility, problemId }) {
    if (problemId.indexOf(DELIMITER) != -1) {
      let ids = problemId.split(DELIMITER).map((id) => parseInt(id));
      state.markers.map((marker) => {
        if (ids.indexOf(marker.problemId) > -1) {
          marker.setVisible(visibility);
        } else {
          marker.setVisible(!visibility);
        }
      });
    } else {
      state.markers.map((marker) => {
        if (marker.problemId === parseInt(problemId)) {
          marker.setVisible(visibility);
        } else {
          marker.setVisible(!visibility);
        }
      });
    }
  },
};

const getters = {
  eventBus(state) {
    return state.eventBus;
  },
  isMobile(state) {
    return state.isMobile;
  },
  map(state) {
    return state.map;
  },
  center(state) {
    return state.center;
  },
  problems(state) {
    return state.problems;
  },
  buildings(state) {
    return state.buildings;
  },
  currentBuilding(state) {
    return state.currentBuilding;
  },
  isEditor(state) {
    return !!(state.sessionUser && state.sessionUser.is_editor);
  },
  markers(state) {
    return state.markers;
  },
  loaded(state) {
    return state.loaded;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
